import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ROLE_LABELS, TENANT_ROLES, type PortalUser, type PortalUserInput, type TenantRole } from '@provalia/shared';
import { Badge, Confirm, Empty, ErrorBox, Field, Loading, Modal, OneTimeSecret, useAsync, useToast } from '../../shared/ui';
import { fmtDateTime } from '../../shared/format';
import { api, type SupplierRow } from '../api';
import { usePortal } from '../context';

type Dialog =
  | { kind: 'new' }
  | { kind: 'edit'; user: PortalUser }
  | { kind: 'reset'; user: PortalUser }
  | { kind: 'delete'; user: PortalUser }
  | { kind: 'invited'; user: PortalUser; invite: InviteResult; resent: boolean };

/** Lo que devuelve la API al invitar: si el correo salió y, si no, el enlace. */
interface InviteResult {
  sent: boolean;
  url: string | null;
  error: string | null;
}

export function Usuarios(): JSX.Element {
  const { me } = usePortal();
  const admin = me.session.role === 'cliente_admin';
  const [params, setParams] = useSearchParams();
  const cardCode = params.get('proveedor') ?? '';
  const toast = useToast();

  const users = useAsync(
    () => api.get<{ users: PortalUser[] }>(`/portal/users${cardCode ? `?cardCode=${encodeURIComponent(cardCode)}` : ''}`),
    [cardCode],
  );
  const suppliers = useAsync(() => api.get<{ suppliers: SupplierRow[] }>('/portal/suppliers'), []);
  const supplierName = (code: string | null): string =>
    code ? (suppliers.data?.suppliers.find((s) => s.cardCode === code)?.name ?? code) : '';

  const [dialog, setDialog] = useState<Dialog | null>(null);

  // Desde «Dar de alta proveedor» se llega con ?nuevo=1 para crear su usuario.
  useEffect(() => {
    if (params.get('nuevo') === '1') {
      setDialog({ kind: 'new' });
      const next = new URLSearchParams(params);
      next.delete('nuevo');
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  /** Compras solo gestiona usuarios de proveedores; su propio personal, el administrador. */
  const canManage = (u: PortalUser): boolean => admin || u.role === 'proveedor';
  const isMe = (u: PortalUser): boolean => u.id === me.session.sub;

  return (
    <div className="stack lg">
      <div className="page-head">
        <div>
          <h1>Usuarios</h1>
          <p className="muted">
            Quién entra en el portal. Un proveedor puede tener varios usuarios (uno por correo): todos ven los mismos pedidos de su empresa.
          </p>
        </div>
        <button className="btn primary" onClick={() => setDialog({ kind: 'new' })}>
          Nuevo usuario
        </button>
      </div>

      <section className="card">
        <div className="card-head">
          <select
            className="select pt-filter-select"
            aria-label="Filtrar por proveedor"
            value={cardCode}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              if (e.target.value) next.set('proveedor', e.target.value);
              else next.delete('proveedor');
              setParams(next, { replace: true });
            }}
          >
            <option value="">Todos los usuarios</option>
            {(suppliers.data?.suppliers ?? []).map((s) => (
              <option key={s.cardCode} value={s.cardCode}>
                {s.name} ({s.cardCode})
              </option>
            ))}
            {cardCode && suppliers.data && !suppliers.data.suppliers.some((s) => s.cardCode === cardCode) ? (
              <option value={cardCode}>{cardCode}</option>
            ) : null}
          </select>
          {users.data ? <span className="subtle">{users.data.users.length} usuarios</span> : null}
        </div>
        {users.error ? (
          <div className="card-body">
            <ErrorBox error={users.error} onRetry={() => void users.reload()} />
          </div>
        ) : !users.data ? (
          <Loading />
        ) : users.data.users.length === 0 ? (
          <Empty icon="👤">
            <strong>{cardCode ? `${supplierName(cardCode)} todavía no tiene usuarios.` : 'No hay usuarios.'}</strong>
            <div>Pulsa «Nuevo usuario» para darle acceso con su correo.</div>
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Correo</th>
                  <th>Perfil</th>
                  <th>Proveedor</th>
                  <th>Estado</th>
                  <th>Último acceso</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.data.users.map((u) => {
                  const manage = canManage(u);
                  const why = manage ? undefined : 'Solo el administrador del portal gestiona los usuarios de su personal';
                  return (
                    <tr key={u.id}>
                      <td>
                        <strong>{u.name}</strong>
                        {isMe(u) ? <span className="subtle"> (tú)</span> : null}
                      </td>
                      <td>{u.email}</td>
                      <td className="nowrap">{ROLE_LABELS[u.role]}</td>
                      <td>{u.cardCode ? `${supplierName(u.cardCode)}` : <span className="subtle">—</span>}</td>
                      <td>
                        {!u.active ? (
                          <Badge tone="bad">Desactivado</Badge>
                        ) : u.passwordSet ? (
                          <Badge tone="ok">Activo</Badge>
                        ) : (
                          <Badge tone="warn">Invitación pendiente</Badge>
                        )}
                      </td>
                      <td className="nowrap">{u.lastLoginAt ? fmtDateTime(u.lastLoginAt) : <span className="subtle">Nunca</span>}</td>
                      <td className="nowrap right" title={why}>
                        <button className="btn sm" disabled={!manage} onClick={() => setDialog({ kind: 'edit', user: u })}>
                          Editar
                        </button>{' '}
                        {u.passwordSet ? (
                          <button className="btn sm" disabled={!manage} onClick={() => setDialog({ kind: 'reset', user: u })}>
                            Cambiar contraseña
                          </button>
                        ) : (
                          <button
                            className="btn sm"
                            disabled={!manage}
                            onClick={() => {
                              void api
                                .post<{ invite: InviteResult }>(`/portal/users/${u.id}/invite`)
                                .then((res) => setDialog({ kind: 'invited', user: u, invite: res.invite, resent: true }))
                                .catch((err: unknown) => toast(err instanceof Error ? err.message : String(err), 'bad'));
                            }}
                          >
                            Reenviar invitación
                          </button>
                        )}{' '}
                        <button
                          className="btn sm danger"
                          disabled={!manage || isMe(u)}
                          title={isMe(u) ? 'No puedes borrar tu propio usuario' : why}
                          onClick={() => setDialog({ kind: 'delete', user: u })}
                        >
                          Borrar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {dialog?.kind === 'new' || dialog?.kind === 'edit' ? (
        <UserModal
          user={dialog.kind === 'edit' ? dialog.user : null}
          admin={admin}
          self={dialog.kind === 'edit' && isMe(dialog.user)}
          suppliers={suppliers.data?.suppliers ?? []}
          defaultCardCode={cardCode}
          onClose={() => setDialog(null)}
          onSaved={(user, invite) => {
            void users.reload();
            if (invite) setDialog({ kind: 'invited', user, invite, resent: false });
            else {
              toast(dialog.kind === 'new' ? `Usuario ${user.email} creado` : `Usuario ${user.email} guardado`, 'ok');
              setDialog(null);
            }
          }}
        />
      ) : null}
      {dialog?.kind === 'reset' ? (
        <Confirm
          title={`Cambiar la contraseña de ${dialog.user.name}`}
          message={
            <p>
              Le mandaremos a <strong>{dialog.user.email}</strong> un correo con un enlace para que elija una contraseña nueva. Su contraseña actual sigue
              valiendo hasta que la cambie. Nadie más que él la conocerá.
            </p>
          }
          confirmLabel="Enviar el enlace"
          onConfirm={async () => {
            await api.post('/portal/account/forgot', { email: dialog.user.email });
            toast(`Enlace enviado a ${dialog.user.email}`, 'ok');
          }}
          onClose={() => setDialog((d) => (d?.kind === 'reset' ? null : d))}
        />
      ) : null}
      {dialog?.kind === 'delete' ? (
        <Confirm
          title={`Borrar a ${dialog.user.name}`}
          message={
            <p>
              <strong>{dialog.user.email}</strong> dejará de poder entrar en el portal. Lo que haya hecho queda en el historial de los pedidos.
            </p>
          }
          confirmLabel="Borrar usuario"
          danger
          onConfirm={async () => {
            await api.del(`/portal/users/${dialog.user.id}`);
            toast(`Usuario ${dialog.user.email} borrado`, 'ok');
            void users.reload();
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'invited' ? (
        <Modal
          title={dialog.resent ? `Invitación para ${dialog.user.name}` : `Usuario creado: ${dialog.user.name}`}
          onClose={() => setDialog(null)}
          footer={
            <button className="btn primary" onClick={() => setDialog(null)}>
              Hecho
            </button>
          }
        >
          {dialog.invite.sent ? (
            <div className="callout ok">
              <div className="title">Invitación enviada a {dialog.user.email}</div>
              Le ha llegado un correo con un enlace para crear su contraseña. El enlace caduca en 7 días; si se le pasa, pulsa «Reenviar invitación».
            </div>
          ) : (
            <div className="stack">
              <div className="callout warn">
                <div className="title">No se ha podido mandar el correo</div>
                {dialog.invite.error}
              </div>
              {dialog.invite.url ? (
                <OneTimeSecret
                  label="Enlace para crear la contraseña"
                  value={dialog.invite.url}
                  note="Házselo llegar por otra vía. Es personal: quien lo tenga puede crear la contraseña de esta cuenta. Caduca en 7 días."
                />
              ) : null}
            </div>
          )}
        </Modal>
      ) : null}
    </div>
  );
}

function UserModal({
  user,
  admin,
  self,
  suppliers,
  defaultCardCode,
  onClose,
  onSaved,
}: {
  user: PortalUser | null;
  admin: boolean;
  self: boolean;
  suppliers: SupplierRow[];
  defaultCardCode: string;
  onClose: () => void;
  onSaved: (user: PortalUser, invite: InviteResult | null) => void;
}): JSX.Element {
  const [email, setEmail] = useState(user?.email ?? '');
  const [name, setName] = useState(user?.name ?? '');
  const [role, setRole] = useState<TenantRole>(user?.role ?? 'proveedor');
  const [cardCode, setCardCode] = useState(user?.cardCode ?? defaultCardCode);
  const [active, setActive] = useState(user?.active ?? true);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const roles: readonly TenantRole[] = admin ? TENANT_ROLES : ['proveedor'];
  const needsSupplier = role === 'proveedor';
  const valid = email.trim() !== '' && name.trim() !== '' && (!needsSupplier || cardCode !== '');

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    const input: PortalUserInput = {
      email: email.trim(),
      name: name.trim(),
      role,
      cardCode: needsSupplier ? cardCode : null,
      active,
      password,
    };
    try {
      if (user) {
        const res = await api.put<{ user: PortalUser }>(`/portal/users/${user.id}`, input);
        onSaved(res.user, null);
      } else {
        const res = await api.post<{ user: PortalUser; invite: InviteResult | null }>('/portal/users', input);
        onSaved(res.user, res.invite);
      }
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <Modal
      title={user ? `Editar a ${user.name}` : 'Nuevo usuario'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button className="btn primary" onClick={() => void submit()} disabled={busy || !valid}>
            {busy ? 'Guardando…' : user ? 'Guardar' : 'Crear usuario'}
          </button>
        </>
      }
    >
      <div className="stack">
        <Field label="Correo" hint="Es el usuario con el que entrará.">
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus={!user} />
        </Field>
        <Field label="Nombre">
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre y apellidos" />
        </Field>
        <Field
          label="Perfil"
          hint={admin ? undefined : 'Como técnico de compras puedes crear usuarios de proveedores. El personal lo gestiona el administrador.'}
        >
          <select className="select" value={role} onChange={(e) => setRole(e.target.value as TenantRole)} disabled={self || roles.length === 1}>
            {roles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </Field>
        {needsSupplier ? (
          <Field label="Proveedor" hint="Verá solo los pedidos de este proveedor. Si no aparece, dalo de alta primero en Proveedores.">
            <select className="select" value={cardCode} onChange={(e) => setCardCode(e.target.value)}>
              <option value="">Elige el proveedor…</option>
              {suppliers.map((s) => (
                <option key={s.cardCode} value={s.cardCode}>
                  {s.name} ({s.cardCode}){s.enabled ? '' : ' · sin acceso'}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        {user ? null : (
          <div className="callout info">
            Le llegará un correo de Provalia con un enlace para que cree su propia contraseña. Nadie más la conocerá.
          </div>
        )}
        {user ? (
          <label className="check">
            <input type="checkbox" checked={active} disabled={self} onChange={(e) => setActive(e.target.checked)} />
            Usuario activo (puede entrar)
          </label>
        ) : null}
        <ErrorBox error={error} />
      </div>
    </Modal>
  );
}
